import { IconAlertTriangle, IconCloudUpload, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";

import { formatBytes } from "../../lib/format";
import { useUploads } from "../../upload/UploadProvider";
import type { FileTransfer } from "../../upload/UploadProvider";

/**
 * What the browser is doing with this file, in one sentence.
 *
 * It never says anything about the *version's* state: that is the server's to
 * publish, and the dataset page reads it from the record. What this describes
 * is the transfer, which is the one thing this browser can see for itself.
 *
 * Args:
 *     transfer: The file on its way.
 *
 * Returns:
 *     One sentence carrying its own numbers.
 */
function sentenceFor(transfer: FileTransfer): string {
  switch (transfer.state) {
    case "sending":
      return `Sending ${transfer.filename} — ${formatBytes(transfer.bytes_sent)} of ${formatBytes(transfer.size_bytes)} received.`;
    case "sealing":
      return `Every part of ${transfer.filename} has arrived. The console is closing the file.`;
    case "sent":
      return `${transfer.filename} has arrived. The console is reading it now — the dataset's trace says when it finishes.`;
    case "failed":
      return transfer.error ?? "The file did not go up.";
  }
}

/**
 * The files this browser is sending, on every screen.
 *
 * The whole point of the wizard is that nobody watches a progress bar for
 * eleven minutes, so the transfer follows the person around the console rather
 * than holding them on one page. Leaving the tab is the one thing that stops
 * it, and the strip says so while it is running.
 *
 * Returns:
 *     One line per file, or nothing at all when none is moving.
 */
export function UploadStrip(): JSX.Element | null {
  const { transfers, cancel, dismiss } = useUploads();

  if (transfers.length === 0) {
    return null;
  }

  return (
    <div className="uploads">
      {transfers.map((transfer) => (
        <div
          className="uploads__row"
          data-state={transfer.state}
          key={transfer.key}
          role="status"
        >
          {transfer.state === "failed" ? (
            <IconAlertTriangle size={14} aria-hidden="true" />
          ) : (
            <IconCloudUpload size={14} aria-hidden="true" />
          )}
          <span className="uploads__text">
            <Link className="uploads__dataset" to={`/datasets/${transfer.dataset_id}`}>
              {transfer.dataset_label}
            </Link>{" "}
            {sentenceFor(transfer)}
            {transfer.state === "sending" ? (
              <span className="uploads__note">
                {" "}
                Keep this tab open — closing it stops the transfer, and you can
                start the file again on the dataset's page.
              </span>
            ) : null}
          </span>
          {transfer.state === "sending" ? (
            <button
              type="button"
              className="btn btn--xs btn--ghost"
              onClick={() => cancel(transfer.key)}
            >
              Stop
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--xs btn--ghost"
              onClick={() => dismiss(transfer.key)}
              aria-label="Hide this line"
            >
              <IconX size={12} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
